import { useCallback } from "react";
import {
  ProfileFormData,
  FormErrors,
  SkillEntry,
} from "../types/profileForm.types";

export const useFormValidation = () => {
  const validateEmail = useCallback((email: string): string | null => {
    if (!email.trim()) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return null;
  }, []);

  const validatePhone = useCallback((phone: string): string | null => {
    if (!phone.trim()) {
      return "Phone number is required";
    }
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, "");
    if (digitsOnly.length < 10) {
      return "Please enter a valid phone number";
    }
    return null;
  }, []);

  const validateName = useCallback((name: string): string | null => {
    if (!name.trim()) {
      return "Name is required";
    }
    if (name.trim().length < 2) {
      return "Name must be at least 2 characters long";
    }
    return null;
  }, []);

  const validatePostalCode = useCallback(
    (postalCode: string): string | null => {
      if (postalCode && postalCode.trim()) {
        // Basic postal code validation (alphanumeric, 3-10 characters)
        const postalRegex = /^[a-zA-Z0-9\s-]{3,10}$/;
        if (!postalRegex.test(postalCode.trim())) {
          return "Please enter a valid postal code";
        }
      }
      return null;
    },
    []
  );

  const validateDateOfBirth = useCallback(
    (dateOfBirth: string): string | null => {
      if (dateOfBirth) {
        const date = new Date(dateOfBirth);
        const today = new Date();
        const minDate = new Date(
          today.getFullYear() - 120,
          today.getMonth(),
          today.getDate()
        );
        const maxDate = new Date(
          today.getFullYear() - 13,
          today.getMonth(),
          today.getDate()
        );

        if (date < minDate) {
          return "Please enter a valid date of birth";
        }
        if (date > maxDate) {
          return "You must be at least 13 years old";
        }
      }
      return null;
    },
    []
  );

  const validateSkillEntry = useCallback(
    (
      skillEntry: SkillEntry
    ): {
      skillName?: string;
      experienceYears?: string;
      selectedStates?: string;
      description?: string;
    } => {
      const errors: any = {};

      // Validate skill name
      if (!skillEntry.skillName || !skillEntry.skillName.trim()) {
        errors.skillName = "Please select a skill";
      }

      // Validate experience years
      if (skillEntry.experienceYears < 0 || skillEntry.experienceYears > 50) {
        errors.experienceYears = "Experience must be between 0 and 50 years";
      }

      // Validate state selection when not Pan India
      if (
        !skillEntry.panIndia &&
        (!skillEntry.selectedStates || skillEntry.selectedStates.length === 0)
      ) {
        errors.selectedStates =
          "Please select at least one state when not serving Pan India";
      }

      // Validate description length
      if (skillEntry.description && skillEntry.description.length > 500) {
        errors.description = "Description must be less than 500 characters";
      }

      return errors;
    },
    []
  );

  const validateSkillEntries = useCallback(
    (skillEntries: SkillEntry[]): FormErrors["skillEntries"] => {
      if (!skillEntries || skillEntries.length === 0) {
        return {};
      }

      const errors: FormErrors["skillEntries"] = {};
      const skillNames = new Set<string>();

      skillEntries.forEach((skillEntry) => {
        const skillErrors = validateSkillEntry(skillEntry);

        // Check for duplicate skills
        if (skillEntry.skillName && skillNames.has(skillEntry.skillName)) {
          skillErrors.skillName = "This skill has already been added";
        } else if (skillEntry.skillName) {
          skillNames.add(skillEntry.skillName);
        }

        if (Object.keys(skillErrors).length > 0) {
          errors[skillEntry.id] = skillErrors;
        }
      });

      return errors;
    },
    [validateSkillEntry]
  );

  const validateField = useCallback(
    (fieldName: string, value: string): string | null => {
      switch (fieldName) {
        case "name":
          return validateName(value);
        case "email":
          return validateEmail(value);
        case "phone":
          return validatePhone(value);
        case "postalCode":
          return validatePostalCode(value);
        case "dateOfBirth":
          return validateDateOfBirth(value);
        default:
          return null;
      }
    },
    [
      validateName,
      validateEmail,
      validatePhone,
      validatePostalCode,
      validateDateOfBirth,
    ]
  );

  const validateForm = useCallback(
    (formData: ProfileFormData): FormErrors => {
      const errors: FormErrors = {};

      // Validate required fields
      const nameError = validateName(formData.name);
      if (nameError) errors.name = nameError;

      const emailError = validateEmail(formData.email);
      if (emailError) errors.email = emailError;

      const phoneError = validatePhone(formData.phone);
      if (phoneError) errors.phone = phoneError;

      // Validate optional fields if they have values
      if (formData.postalCode) {
        const postalError = validatePostalCode(formData.postalCode);
        if (postalError) errors.postalCode = postalError;
      }

      if (formData.dateOfBirth) {
        const dobError = validateDateOfBirth(formData.dateOfBirth);
        if (dobError) errors.dateOfBirth = dobError;
      }

      // Validate skill entries
      if (formData.skillEntries && formData.skillEntries.length > 0) {
        const skillErrors = validateSkillEntries(formData.skillEntries);
        // if (Object.keys(skillErrors).length > 0) {
        //   errors.skillEntries = skillErrors;
        // }
      }

      return errors;
    },
    [
      validateName,
      validateEmail,
      validatePhone,
      validatePostalCode,
      validateDateOfBirth,
      validateSkillEntries,
    ]
  );

  const isFormValid = useCallback((errors: FormErrors): boolean => {
    return Object.keys(errors).length === 0;
  }, []);

  return {
    validateField,
    validateForm,
    isFormValid,
    validateEmail,
    validatePhone,
    validateName,
    validatePostalCode,
    validateDateOfBirth,
    validateSkillEntry,
    validateSkillEntries,
  };
};
